/**
 * Public QR payloads for phone-camera → landing page.
 * Opaque codes (`company:`, `booth:`, …) stay the internal scan format;
 * printed QRs become https://…/go/… URLs that open the channel chooser.
 */

export type PublicQrKind = "company" | "booth" | "booth_slot" | "badge" | "product";

export type ParsedPublicQr = {
  kind: PublicQrKind;
  opaque: string;
  eventId?: string;
  companyId?: string;
  booth?: string;
  repId?: string;
  productId?: string;
};

function trimBase(url: string): string {
  return url.replace(/\/+$/, "");
}

/** Internal opaque → printable absolute URL (for QR images). */
export function toPublicQrUrl(publicWebUrl: string, opaque: string): string {
  const base = trimBase(publicWebUrl);
  const code = opaque.trim();
  if (!code) return base + "/go";

  if (code.startsWith("http://") || code.startsWith("https://")) {
    return code;
  }

  if (code.startsWith("booth:")) {
    const parts = code.split(":");
    const eventId = parts[1] ?? "";
    const companyId = parts.slice(2).join(":") || parts[parts.length - 1] || "";
    if (eventId && companyId) {
      return `${base}/go/booth/${encodeURIComponent(eventId)}/${encodeURIComponent(companyId)}`;
    }
  }
  if (code.startsWith("boothslot:")) {
    const parts = code.split(":");
    const eventId = parts[1] ?? "";
    const booth = parts.slice(2).join(":") || "";
    if (eventId && booth) {
      return `${base}/go/booth-slot/${encodeURIComponent(eventId)}/${encodeURIComponent(booth)}`;
    }
  }
  if (code.startsWith("company:")) {
    const id = code.slice("company:".length);
    if (id) return `${base}/go/company/${encodeURIComponent(id)}`;
  }
  if (code.startsWith("badge:")) {
    const id = code.slice("badge:".length);
    if (id) return `${base}/go/badge/${encodeURIComponent(id)}`;
  }
  if (code.startsWith("product:")) {
    const id = code.slice("product:".length);
    if (id) return `${base}/go/product/${encodeURIComponent(id)}`;
  }

  return `${base}/go/c/${encodeURIComponent(code)}`;
}

/** Prefer public URL for printing; keep opaque if no public base configured. */
export function toPrintableQrValue(opaque: string, publicWebUrl?: string | null): string {
  const base = publicWebUrl?.trim();
  if (!base) return opaque;
  return toPublicQrUrl(base, opaque);
}

/**
 * Parse landing path / full URL / opaque code → structured target.
 * Accepts:
 * - booth:evt:company
 * - https://host/go/booth/evt/company
 * - /go/company/id
 */
export function parsePublicQrInput(raw: string): ParsedPublicQr | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let path = trimmed;
  try {
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      const u = new URL(trimmed);
      path = u.pathname + (u.search || "");
    }
  } catch {
    // keep as path/opaque
  }

  const goMatch = path.match(/\/go\/([^?#]+)/i);
  if (goMatch) {
    const segments = goMatch[1]!.split("/").map((s) => decodeURIComponent(s));
    const [kind, a, b] = segments;
    if (kind === "booth" && a && b) {
      return {
        kind: "booth",
        opaque: `booth:${a}:${b}`,
        eventId: a,
        companyId: b,
      };
    }
    if ((kind === "booth-slot" || kind === "boothslot") && a && b) {
      return {
        kind: "booth_slot",
        opaque: `boothslot:${a}:${b}`,
        eventId: a,
        booth: b,
      };
    }
    if (kind === "company" && a) {
      return { kind: "company", opaque: `company:${a}`, companyId: a };
    }
    if (kind === "badge" && a) {
      return { kind: "badge", opaque: `badge:${a}`, repId: a };
    }
    if (kind === "product" && a) {
      return { kind: "product", opaque: `product:${a}`, productId: a };
    }
    if (kind === "c" && a) {
      return parseOpaqueCode(a);
    }
  }

  return parseOpaqueCode(trimmed);
}

function parseOpaqueCode(code: string): ParsedPublicQr | null {
  if (code.startsWith("boothslot:")) {
    const parts = code.split(":");
    const eventId = parts[1];
    const booth = parts.slice(2).join(":");
    if (!eventId || !booth) return null;
    return { kind: "booth_slot", opaque: code, eventId, booth };
  }
  if (code.startsWith("booth:")) {
    const parts = code.split(":");
    const eventId = parts[1];
    const companyId = parts.slice(2).join(":") || parts[parts.length - 1];
    if (!eventId || !companyId) return null;
    return { kind: "booth", opaque: code, eventId, companyId };
  }
  if (code.startsWith("company:")) {
    const companyId = code.slice("company:".length);
    if (!companyId) return null;
    return { kind: "company", opaque: code, companyId };
  }
  if (code.startsWith("badge:")) {
    const repId = code.slice("badge:".length);
    if (!repId) return null;
    return { kind: "badge", opaque: code, repId };
  }
  if (code.startsWith("product:")) {
    const productId = code.slice("product:".length);
    if (!productId) return null;
    return { kind: "product", opaque: code, productId };
  }
  return null;
}

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  const b64 =
    typeof globalThis.btoa === "function"
      ? globalThis.btoa(bin)
      : bytesToBase64(bytes);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function fromBase64Url(b64url: string): string {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const padded = b64 + pad;
  const bin =
    typeof globalThis.atob === "function"
      ? globalThis.atob(padded)
      : base64ToBin(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

const B64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/** Fallback when btoa is unavailable (no Node Buffer dependency). */
function bytesToBase64(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = i + 1 < bytes.length ? bytes[i + 1]! : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2]! : 0;
    const triple = (a << 16) | (b << 8) | c;
    out += B64_ALPHABET[(triple >> 18) & 63];
    out += B64_ALPHABET[(triple >> 12) & 63];
    out += i + 1 < bytes.length ? B64_ALPHABET[(triple >> 6) & 63] : "=";
    out += i + 2 < bytes.length ? B64_ALPHABET[triple & 63] : "=";
  }
  return out;
}

function base64ToBin(b64: string): string {
  const clean = b64.replace(/=+$/g, "");
  let bin = "";
  for (let i = 0; i < clean.length; i += 4) {
    const enc1 = B64_ALPHABET.indexOf(clean[i]!);
    const enc2 = B64_ALPHABET.indexOf(clean[i + 1]!);
    const enc3 = i + 2 < clean.length ? B64_ALPHABET.indexOf(clean[i + 2]!) : -1;
    const enc4 = i + 3 < clean.length ? B64_ALPHABET.indexOf(clean[i + 3]!) : -1;
    const bitmap = (enc1 << 18) | (enc2 << 12) | ((enc3 < 0 ? 0 : enc3) << 6) | (enc4 < 0 ? 0 : enc4);
    bin += String.fromCharCode((bitmap >> 16) & 255);
    if (enc3 >= 0) bin += String.fromCharCode((bitmap >> 8) & 255);
    if (enc4 >= 0) bin += String.fromCharCode(bitmap & 255);
  }
  return bin;
}

/** Compact MAX start_param (base64url of opaque, prefix `g.`). */
export function encodeQrStartParam(opaque: string): string {
  return `g.${toBase64Url(opaque)}`;
}

export function decodeQrStartParam(startParam: string): string | null {
  const raw = startParam.trim();
  if (!raw.startsWith("g.")) return null;
  try {
    return fromBase64Url(raw.slice(2));
  } catch {
    return null;
  }
}

export function buildMaxStartAppLink(botDeepLinkBase: string, opaque: string): string {
  const base = botDeepLinkBase.replace(/\?startapp.*$/i, "").replace(/\/+$/, "");
  const param = encodeQrStartParam(opaque);
  const sep = base.includes("?") ? "&" : "?";
  // max.ru bot links use startapp=
  if (/startapp=/i.test(botDeepLinkBase)) {
    return `${base.split("?")[0]}?startapp=${encodeURIComponent(param)}`;
  }
  return `${base}${sep}startapp=${encodeURIComponent(param)}`;
}
